import React, { useState, useEffect, useCallback } from 'react';
import './AtlasDateSelector.scss';
import DateSelector from '../../DateSelector/DateSelector';
import { Sphere } from 'types/global';
import { SchemaProperty } from 'types/schema';
import { PermissionList } from '../../../../../types/sharedTypes';
import { useSchemaType } from '../../../../hooks/useSchemaType';

interface AtlasDateSelectorProps {
  sphere: Sphere;
  dateProperty: string;
  className?: string;
  isFriendly?: boolean;
  fetchPermissionList: (sphereId: string, nodeLabel: string, permissions?: string[]) => Promise<PermissionList | null>;
  onDateChanged: (sphereId: string, propertyName: string, newValue: string | null) => Promise<void>;
  style?: React.CSSProperties;
}

const AtlasDateSelector: React.FC<AtlasDateSelectorProps> = ({
  sphere,
  dateProperty,
  // Matches `ATLAS_MOLECULE_CATALOG.AtlasDateSelector`'s `defaultValue: true` and
  // `AtlasSpherePropertyMolecule`'s default — this default previously disagreed (`false`),
  // so a caller relying on either component's default saw a different date format.
  isFriendly = true,
  className = '',
  fetchPermissionList,
  onDateChanged,
  style,
}) => {
  const schemaType = Array.isArray(sphere.properties.type)
    ? sphere.properties.type[0]
    : sphere.properties.type;

  const [isReadOnly, setIsReadOnly] = useState(false);
  const [hasPermission, setHasPermission] = useState<boolean | null>(null);
  const [localDate, setLocalDate] = useState<Date | null>(null);
  const { schema: matchingSchema } = useSchemaType(schemaType ?? null);

  // Tokenised so the read-only cell follows the theme — a literal here rendered a light
  // panel in dark mode, and this component is now the renderer for every date aspect
  // column, not just the retired standalone date column.
  const readOnlyStyle = isReadOnly
  ? { background: 'var(--surface-subtle)', cursor: 'not-allowed' }
  : {};

  useEffect(() => {
    if (sphere?.properties) {
      const initialDate = sphere.properties[dateProperty];
      setLocalDate(initialDate ? new Date(initialDate as string) : null);

      if (matchingSchema) {
        const schemaProp = matchingSchema.properties.find(
          (p: SchemaProperty) => p.name === dateProperty
        );
        if (schemaProp) {
          const isExisting = !!sphere?.id;
          const isLocked = schemaProp.read_only || (schemaProp.immutable && isExisting);
          setIsReadOnly(!!isLocked);
        } else {
          setIsReadOnly(true);
        }
      } else {
        setIsReadOnly(true);
      }
    }
  }, [sphere, matchingSchema, dateProperty]);

  const permissionCheck = useCallback(async (): Promise<boolean> => {
    if (isReadOnly) return false;
    try {
      const permissionList = await fetchPermissionList(sphere.id, sphere.label || '', ['edit']);
      const canEdit = permissionList?.permissions?.edit || false;
      setHasPermission(canEdit);
      return canEdit;
    } catch {
      // Fail closed — an unreadable permission response renders the cell read-only.
      setHasPermission(false);
      return false;
    }
  }, [fetchPermissionList, sphere.id, sphere.label, isReadOnly]);

  const handleDateChange = useCallback(
    async (newDate: Date | null) => {
      const previousDate = localDate;
      setLocalDate(newDate); // Optimistic update

      try {
        const dateString = newDate ? newDate.toISOString() : null;
        await onDateChanged(sphere.id, dateProperty, dateString); // Delegate to parent
      } catch (error) {
        // Reverting alone makes a failed write indistinguishable from the user changing
        // their mind, so the failure is surfaced rather than dropped.
        console.error('[AtlasDateSelector] Failed to save date', {
          sphereId: sphere.id,
          dateProperty,
          error,
        });
        setLocalDate(previousDate); // Revert on failure
      }
    },
    [dateProperty, localDate, onDateChanged, sphere.id]
  );

  if (!dateProperty) {
    return <div className={`atlas-date-selector ${className}`} style={readOnlyStyle} />;
  }

  return (
    <div
      className={`atlas-date-selector ${className}`}
      style={{ ...readOnlyStyle, ...style }}
    >
      <DateSelector
      initialDate={localDate ? localDate.toISOString() : null}
      onDateChange={handleDateChange}
      isFriendly={isFriendly}
      isReadOnly={isReadOnly || hasPermission === false}
      permissionCheck={permissionCheck}
      />
    </div>
  );
};

export default AtlasDateSelector;
